<template>
  <CCard class="p-5">
    <div class="flex items-center justify-between mb-3">
      <div class="flex-y-center space-x-2">
        <h1 class="text-dark text-lg font-semibold">
          {{ bot?.strategy ? bot?.strategy : bot?.title }}
        </h1>
        <Badge
          v-if="bot?.is_pack_bot"
          class="w-auto shrink-0 mt-px capitalize flex items-center"
          variant="default"
        >
          <span>{{ $t("saraf_pack") }}</span>
        </Badge>
      </div>
      <template v-if="bot?.is_deleted">
        <Badge
          class="w-auto shrink-0 mt-px capitalize flex items-center"
          variant="destructive"
        >
          <span class="icon-trash text-base mr-1" />
          <span>{{ $t("deleted") }}</span>
        </Badge>
      </template>
      <template v-else>
        <template v-if="bot?.is_stop_buy">
          <div class="flex-y-center gap-2">
            <Tooltip :text="$t('is_stopped_by')">
              <button class="icon-info-circle text-xl text-yellow" />
            </Tooltip>
            <CBadge color="yellow" :status="$t('is_stopped_by')" />
          </div>
        </template>
        <template v-else>
          <CBadge color="green" :status="$t('active')" />
        </template>
      </template>
    </div>
    <div
      class="grid grid-cols-2 gap-4 border-b border-dashed border-gray-50 mb-2 pb-2"
    >
      <CBotRow
        v-for="(botValue, idx) of botMainStatistics"
        :key="idx"
        :id="idx"
        :title="botValue?.title"
        :value="botValue?.value"
        :percent="botValue?.percent"
      />
    </div>
    <CBotRow
      v-for="(botValue, idx) of botStatistics"
      :key="idx"
      :id="idx"
      row
      :title="botValue?.title"
      :value="botValue?.value"
      :percent="botValue?.percent"
    >
      <template #content>
        <div v-if="botValue?.icon" class="flex-y-center gap-1">
          <div v-html="botValue?.icon" class="bot-icon" />
          <div class="whitespace-nowrap">{{ botValue?.value }}</div>
        </div>
      </template>
    </CBotRow>
    <CButton
      class="mt-3 text-xs text-dark-100"
      :text="$t('go')"
      :variant="'secondary'"
      :icon="'icon-chevron-curved -rotate-90'"
      @click="checkIsPlan(bot?.id)"
    />
  </CCard>
</template>

<script setup lang="ts">
import { useConvertToUserTimezone } from "@/composables/useConvertToUserTimezone";
import CButton from "@/components/Common/CButton.vue";
import CCard from "@/components/Card/CardWrapper.vue";
import router from "@/router";
import { IBot } from "@/modules/Bots/types";
import { computed, ref, watch } from "vue";
import { useAuthStore } from "@/modules/Auth/stores";
import { useBotStore } from "@/modules/Bots/stores";
import CBotRow from "@/modules/Bots/components/CBotRow.vue";
import Badge from "../../../components/ui/badge/Badge.vue";
import CBadge from "@/components/Common/CBadge.vue";
import Tooltip from "@/components/Common/Tooltip.vue";
import { formatMoneyExact } from "@/utils";
import { useLocalizedNavigation } from "@/composables/useLocalizedNavigation";

interface Props {
  bot: IBot;
}
const props = defineProps<Props>();
const emit = defineEmits<{
  (e: "showPlan"): void;
}>();

const { convertToUserTimezone } = useConvertToUserTimezone();
const { navigateTo } = useLocalizedNavigation();

const botMainStatistics = computed(() => [
  {
    title: "strategy",
    value: props?.bot?.strategy ? props?.bot?.strategy : props?.bot?.title,
  },
  {
    title: "all_trades",
    value: props?.bot?.total_orders,
  },
  {
    title: "active_trades",
    value: props?.bot?.coins_count,
  },
  {
    title: "closed_trade_count",
    value: props?.bot?.closed_orders,
  },
]);
const botStatistics = computed(() => [
  {
    title: "pl_on_closed_trades",
    value: formatMoneyExact(props?.bot?.profit_closed_coin, 2) + " USDT",
    percent: props?.bot?.profit_closed_percent_mean,
  },
  {
    title: "pl_on_all_trades",
    value: formatMoneyExact(props?.bot?.profit_all_coin, 2) + " USDT",
    percent: props?.bot?.profit_all_percent_mean,
  },
  {
    title: "bot_started",
    value: convertToUserTimezone(props?.bot?.bot_start_date),
  },
  {
    title: "first_trade_opened",
    value: convertToUserTimezone(props?.bot?.first_trade_date),
  },
  {
    title: "latest_trade_opened",
    value: convertToUserTimezone(props?.bot?.latest_trade_date),
  },
  {
    title: "best_performing",
    value: props?.bot?.best_pair?.length
      ? props?.bot?.best_pair + ": " + props?.bot?.best_rate + "%"
      : "N/A",
    icon: props?.bot?.best_pair_icon,
  },
  {
    title: "win_loss",
    value: props?.bot?.winning_trades + " / " + props?.bot?.losing_trades,
  },
]);

const botStore = useBotStore();
const store = useAuthStore();
const user = computed(() => store.user);

const botIsRunning = ref(props?.bot?.is_running ?? false);

const checkIsPlan = (item: number) => {
  const planTitle = user.value?.plan;
  const planLeftDays = user.value?.plan_left_days;

  if ((planTitle !== null && planTitle !== "") || planLeftDays) {
    navigateTo({ name: "BotsSingle", params: { botId: item } });
  } else {
    emit("showPlan");
  }
};

watch(
  () => botIsRunning.value,
  (val) => {
    botStore.triggerBotStatus(props?.bot?.id, val);
  },
);
</script>

<style>
.bot-icon > svg {
  width: 18px !important;
  height: 18px !important;
}
</style>
